import { FlagIcon } from '@design/components/flag-icon/FlagIcon';
import { RegionIcon } from '@design/components/region-icon/RegionIcon';
import clsx from 'clsx';
import type { ReactNode } from 'react';

import styles from './GraphIcon.module.scss';

export type GraphIconVariant =
  | 'amber-circle'
  | 'indigo-triangle'
  | 'purple-pentagon'
  | 'teal-hexagon'
  | 'crimson-diamond'
  | 'olive-circle'
  | 'gold-square';

/**
 * Visual state of a shape node: a plain shape (`default`), the same shape with
 * the hover fill + grow (`hover`), or the circular badge that holds a `logo`
 * (`active`). Hover is caller-driven, so a non-interactive node is simply never
 * put into the `hover` state.
 */
export type GraphIconShapeState = 'default' | 'hover' | 'active';

export type GraphIconData =
  | {
      kind: 'shape';
      variant: GraphIconVariant;
      /** Rendered inside the badge when `state` is `active`; ignored otherwise. */
      logo?: ReactNode;
      /** Defaults to `default`. */
      state?: GraphIconShapeState;
    }
  | { kind: 'brands_and_products'; value: string }
  | { kind: 'flag'; countryCode: string }
  | { kind: 'region'; regionCode: string }
  | { kind: 'custom'; content: ReactNode };

const ACTIVE_RING_GAP = 3;
/** Inset between the outer (accent stroke) and inner (fill + black stroke) shapes. */
const SHAPE_INSET = 1;
const AMBER_CIRCLE_SHAPE_INSET = 2;
/** Floor so a tiny `size` can never yield a zero/negative inner radius. */
const MIN_INNER_RADIUS = 1;
/** Axis-aligned square inscribed in the shape's circumradius. */
const SQUARE_SCALE = 1 / Math.SQRT2;
const ROUNDED_SQUARE_SCALE = 0.8;
const GOLD_SQUARE_CORNER_RATIO = 0.2;
const OLIVE_CIRCLE_CORNER_RATIO = 0.5;
/** Indigo tip angle (equilateral would be 60°). */
const INDIGO_TRIANGLE_APEX_DEGREES = 50;

export const activeGraphIconSize = (size: number) => size + ACTIVE_RING_GAP * 2;

export interface GraphIconProps {
  data: GraphIconData;
  size: number;
  className?: string;
}

/** Applies the icon accent to containers that render related visuals. */
export const VARIANT_CLASS: Record<GraphIconVariant, string> = {
  'amber-circle': styles.amberCircle,
  'indigo-triangle': styles.indigoTriangle,
  'purple-pentagon': styles.purplePentagon,
  'teal-hexagon': styles.tealHexagon,
  'crimson-diamond': styles.crimsonDiamond,
  'olive-circle': styles.oliveCircle,
  'gold-square': styles.goldSquare,
};

const polygonPoints = (sides: number, radius: number): string =>
  Array.from({ length: sides }, (_, index) => {
    const angle = -Math.PI / 2 + (index * Math.PI * 2) / sides;
    return `${Math.cos(angle) * radius},${Math.sin(angle) * radius}`;
  }).join(' ');

/** Isosceles triangle on the circumcircle, tip up, with the given apex angle. */
const trianglePoints = (radius: number, apexDegrees: number): string => {
  const apexRad = (apexDegrees * Math.PI) / 180;
  return [-Math.PI / 2, Math.PI / 2 - apexRad, Math.PI / 2 + apexRad]
    .map(angle => `${Math.cos(angle) * radius},${Math.sin(angle) * radius}`)
    .join(' ');
};

const squareRect = (radius: number, scale: number, cornerRatio: number) => {
  const half = radius * scale;

  return {
    height: half * 2,
    width: half * 2,
    x: -half,
    y: -half,
    rx: cornerRatio > 0 ? half * cornerRatio : undefined,
  };
};

/**
 * Two shapes matching the Figma export: outer (accent stroke) + inset inner
 * (50% accent fill, background stroke).
 */
const NodeShape = ({
  variant,
  size,
}: {
  variant: GraphIconVariant;
  size: number;
}) => {
  const radius = size / 2;
  const innerRadius = Math.max(
    radius -
      (variant === 'amber-circle' ? AMBER_CIRCLE_SHAPE_INSET : SHAPE_INSET),
    MIN_INNER_RADIUS
  );

  const shapeLayer = (layerRadius: number, className: string): ReactNode => {
    switch (variant) {
      case 'amber-circle':
        return <circle className={className} r={layerRadius} />;
      case 'indigo-triangle':
        return (
          <polygon
            className={className}
            points={trianglePoints(layerRadius, INDIGO_TRIANGLE_APEX_DEGREES)}
          />
        );
      case 'purple-pentagon':
        return (
          <polygon
            className={className}
            points={polygonPoints(5, layerRadius)}
          />
        );
      case 'teal-hexagon':
        return (
          <polygon
            className={className}
            points={polygonPoints(6, layerRadius)}
          />
        );
      case 'crimson-diamond':
        return (
          <polygon
            className={className}
            points={polygonPoints(4, layerRadius)}
          />
        );
      case 'gold-square':
        return (
          <rect
            className={className}
            {...squareRect(layerRadius, SQUARE_SCALE, GOLD_SQUARE_CORNER_RATIO)}
          />
        );
      case 'olive-circle':
        return (
          <rect
            className={className}
            {...squareRect(
              layerRadius,
              ROUNDED_SQUARE_SCALE,
              OLIVE_CIRCLE_CORNER_RATIO
            )}
          />
        );
    }
  };

  return (
    <>
      {shapeLayer(radius, styles.shapeOutline)}
      {shapeLayer(innerRadius, styles.shape)}
    </>
  );
};

/**
 * Graph visual: default geometric shape (by variant), active circular badge
 * with `logo`, or entity content (image, brands_and_products, flag, region, custom).
 */
export const GraphIcon = ({ data, size, className }: GraphIconProps) => {
  switch (data.kind) {
    case 'shape': {
      const state = data.state ?? 'default';

      if (state === 'active') {
        const outerSize = activeGraphIconSize(size);

        return (
          <div
            className={clsx(
              styles.root,
              VARIANT_CLASS[data.variant],
              styles.active,
              className
            )}
            style={{ width: outerSize, height: outerSize }}
          >
            <div
              className={styles.activeBadge}
              style={{ width: size, height: size }}
            >
              {data.logo}
            </div>
          </div>
        );
      }

      return (
        <g
          className={clsx(
            styles.shapeRoot,
            VARIANT_CLASS[data.variant],
            className
          )}
          data-state={state}
        >
          <NodeShape size={size} variant={data.variant} />
        </g>
      );
    }
    case 'flag':
      return (
        <FlagIcon
          className={className}
          countryCode={data.countryCode}
          customSize={size}
          variant="circular"
        />
      );
    case 'region':
      return <RegionIcon region={data.regionCode} size={size} />;
    case 'brands_and_products':
      return (
        <span
          className={clsx(
            styles.letter,
            VARIANT_CLASS['crimson-diamond'],
            className
          )}
          style={{ width: size, height: size }}
        >
          {data.value}
        </span>
      );
    case 'custom':
      return data.content;
  }
};
